import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'https://api.planmyplate.io';

// Configure axios defaults
axios.defaults.baseURL = API_URL;
axios.defaults.headers.common['Accept'] = 'application/json';
axios.defaults.headers.common['Content-Type'] = 'application/json';

// Add request interceptor for authentication
axios.interceptors.request.use((config) => {
  const token = localStorage.getItem('admin_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
}, (error) => {
  return Promise.reject(error);
});

export interface DiscountCampaign {
  id: string;
  name: string;
  code: string;
  type: "percentage" | "fixed";
  value: number;
  description?: string;
  status: "active" | "inactive" | "expired";
  usage_limit: number | null;
  used_count: number;
  starts_at: string;
  expires_at: string | null;
  created_at: string;
  updated_at: string;
  created_by: string;
  target_plans: string[];
  minimum_plan_duration: number | null;
}

export interface CreateCampaignRequest {
  name: string;
  code?: string;
  type: "percentage" | "fixed";
  value: number;
  description?: string;
  usage_limit?: number | null;
  starts_at?: string;
  expires_at?: string | null;
  target_plans?: string[];
  minimum_plan_duration?: number | null;
}

export interface UpdateCampaignRequest {
  name?: string;
  code?: string;
  type?: "percentage" | "fixed";
  value?: number;
  description?: string;
  usage_limit?: number | null;
  starts_at?: string;
  expires_at?: string | null;
  target_plans?: string[];
  minimum_plan_duration?: number | null;
}

export interface CampaignResponse {
  success: boolean;
  campaign?: DiscountCampaign;
  error?: string;
}

export interface CampaignsResponse {
  success: boolean;
  campaigns?: DiscountCampaign[];
  error?: string;
}

export interface CampaignStatsResponse {
  success: boolean;
  stats?: {
    total_campaigns: number;
    active_campaigns: number;
    total_usage: number;
    total_discount_given: number;
    top_campaigns: Array<{
      id: string;
      name: string;
      code: string;
      used_count: number;
      total_discount: number;
    }>;
  };
  error?: string;
}

class AdminCampaignService {
  /**
   * Get all discount campaigns
   */
  async getCampaigns(): Promise<CampaignsResponse> {
    try {
      const response = await axios.get('/api/admin/discount-campaigns');
      return response.data;
    } catch (error: any) {
      console.error('Error fetching campaigns:', error);
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to fetch campaigns'
      };
    }
  }

  /**
   * Get a specific campaign by ID
   */
  async getCampaign(campaignId: string): Promise<CampaignResponse> {
    try {
      const response = await axios.get(`/api/admin/discount-campaigns/${campaignId}`);
      return response.data;
    } catch (error: any) {
      console.error('Error fetching campaign:', error);
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to fetch campaign'
      };
    }
  }

  /**
   * Create a new discount campaign
   */
  async createCampaign(data: CreateCampaignRequest): Promise<CampaignResponse> {
    try {
      const response = await axios.post('/api/admin/discount-campaigns', data);
      return response.data;
    } catch (error: any) {
      console.error('Error creating campaign:', error);
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to create campaign'
      };
    }
  }

  /**
   * Update an existing campaign
   */
  async updateCampaign(campaignId: string, data: UpdateCampaignRequest): Promise<CampaignResponse> {
    try {
      const response = await axios.put(`/api/admin/discount-campaigns/${campaignId}`, data);
      return response.data;
    } catch (error: any) {
      console.error('Error updating campaign:', error);
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to update campaign'
      };
    }
  }

  /**
   * Delete a campaign
   */
  async deleteCampaign(campaignId: string): Promise<{ success: boolean; error?: string }> {
    try {
      const response = await axios.delete(`/api/admin/discount-campaigns/${campaignId}`);
      return response.data;
    } catch (error: any) {
      console.error('Error deleting campaign:', error);
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to delete campaign'
      };
    }
  }

  /**
   * Update campaign status (active/inactive)
   */
  async updateCampaignStatus(campaignId: string, status: "active" | "inactive"): Promise<CampaignResponse> {
    try {
      const response = await axios.patch(`/api/admin/discount-campaigns/${campaignId}/status`, { status });
      return response.data;
    } catch (error: any) {
      console.error('Error updating campaign status:', error);
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to update campaign status'
      };
    }
  }

  /**
   * Get campaign usage statistics
   */
  async getCampaignStats(): Promise<CampaignStatsResponse> {
    try {
      const response = await axios.get('/api/admin/discount-campaigns/stats');
      return response.data;
    } catch (error: any) {
      console.error('Error fetching campaign stats:', error);
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to fetch campaign stats'
      };
    }
  }

  /**
   * Get campaign usage history
   */
  async getCampaignUsage(campaignId: string, page: number = 1, limit: number = 50): Promise<any> {
    try {
      const response = await axios.get(`/api/admin/discount-campaigns/${campaignId}/usage`, {
        params: { page, limit }
      });
      return response.data;
    } catch (error: any) {
      console.error('Error fetching campaign usage:', error);
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to fetch campaign usage'
      };
    }
  }

  /**
   * Validate a discount code (public endpoint for frontend use)
   */
  async validateDiscountCode(code: string, plan?: string, billing?: string): Promise<any> {
    try {
      const response = await axios.post('/api/discount-codes/validate', {
        code,
        plan,
        billing
      });
      return response.data;
    } catch (error: any) {
      console.error('Error validating discount code:', error);
      return {
        success: false,
        error: error.response?.data?.error || 'Invalid discount code'
      };
    }
  }

  /**
   * Apply discount code during subscription (public endpoint)
   */
  async applyDiscountCode(code: string, plan: string, billing: string): Promise<any> {
    try {
      const response = await axios.post('/api/discount-codes/apply', {
        code,
        plan,
        billing
      });
      return response.data;
    } catch (error: any) {
      console.error('Error applying discount code:', error);
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to apply discount code'
      };
    }
  }

  /**
   * Export campaign data to CSV
   */
  async exportCampaigns(): Promise<{ success: boolean; url?: string; error?: string }> {
    try {
      const response = await axios.get('/api/admin/discount-campaigns/export', {
        responseType: 'blob'
      });
      
      // Create download URL
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      
      return {
        success: true,
        url: url
      };
    } catch (error: any) {
      console.error('Error exporting campaigns:', error);
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to export campaigns'
      };
    }
  }

  /**
   * Generate a unique discount code
   */
  async generateDiscountCode(): Promise<{ success: boolean; code?: string; error?: string }> {
    try {
      const response = await axios.get('/api/admin/discount-campaigns/generate-code');
      return response.data;
    } catch (error: any) {
      console.error('Error generating discount code:', error);
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to generate discount code'
      };
    }
  }
}

export const adminCampaignService = new AdminCampaignService();